import { collection } from '@/infra/collection'
import {
  JobTaxAssistant,
  type JobTaxAssistantProps,
} from '@/features/jobs/taxes/assistant/JobTaxAssistant'
import { Id } from '@/infra/id'
import { memoizeAsync } from '@/utils/memoize-decorator'
import type { JobsApi } from '@/features/jobs/api/job-api'
import {
  GetJobTaxAssistantRequest,
  type JobTaxAssistant as JobTaxAssistantProto,
  JobTaxAssistantReplyStatus as JobTaxAssistantReplyStatusProto,
} from '@taxfyle/api-internal/internal/job_tax_assistant_pb'
import { timestampToISO } from '@/utils/grpc-util'
import { safeParseDate } from '@/utils/date-util'
import { match } from '@/utils/pattern-match-util'
import type { TaxAssistantReplyStatus } from '@/features/jobs/taxes/assistant/tax-assistant-reply-status'
import { tap } from 'rxjs/operators'
import type { RealtimeEvent as RealtimeEventProto } from '@taxfyle/api-internal/internal/realtime_pb'
import { RpcError, StatusCode } from 'grpc-web'

/**
 * Store for job tax assistants.
 */
export class JobTaxAssistantStore {
  /**
   * Loaded job tax assistants.
   */
  readonly taxAssistants = collection({
    create: () => new JobTaxAssistant(),
    getDataId: (d) => d.jobId,
    getModelId: (m) => m.jobId,
  })

  constructor(private readonly _api: JobsApi) {
    this._api.realtime$
      .pipe(
        tap((evt) => {
          this._onRemoteUpdate(evt)
        }),
      )
      .subscribe()
  }

  /**
   * Gets a tax assistant for the specified job if one is loaded.
   * @param jobId
   */
  getTaxAssistant(jobId: Id): JobTaxAssistant | null {
    return this.taxAssistants.get(jobId) ?? null
  }

  /**
   * Fetches the tax assistant for the specified job.
   *
   * @param jobId
   */
  @memoizeAsync()
  async fetch(jobId: Id): Promise<JobTaxAssistant | null> {
    jobId = Id.assert(jobId)
    const request = new GetJobTaxAssistantRequest().setJobId(jobId)

    const response = await this._api.taxAssistant
      .getJobTaxAssistant(request)
      .catch((err) => {
        if (err instanceof RpcError && err.code === StatusCode.NOT_FOUND) {
          return null
        }

        console.error('Error loading the tax assistant', err)
        throw err
      })

    if (!response) {
      return null
    }
    const props = mapTaxAssistantProtoToProps(response)
    return this.taxAssistants.set(props)
  }

  /**
   * Handles a real-time update for the tax assistant.
   */
  private _onRemoteUpdate(evt: RealtimeEventProto) {
    const assistantProto = evt.getJobTaxAssistantUpdated()?.getAssistant()
    if (!assistantProto) {
      return
    }

    const assistant = mapTaxAssistantProtoToProps(assistantProto)
    this.taxAssistants.set(assistant)
  }
}

function mapTaxAssistantProtoToProps(
  proto: JobTaxAssistantProto,
): JobTaxAssistantProps {
  return {
    jobId: proto.getJobId(),
    active: proto.getActive(),
    externalReplyId: proto.getExternalReplyId()?.getValue() ?? null,
    lastUpdated: safeParseDate(timestampToISO(proto.getUpdateTime())),
    replyStatus: mapReplyStatusProtoToStatus(proto.getReplyStatus()),
  }
}

function mapReplyStatusProtoToStatus(
  proto: JobTaxAssistantReplyStatusProto,
): TaxAssistantReplyStatus {
  return match(proto, {
    [JobTaxAssistantReplyStatusProto.IDLE]: 'Idle',
    [JobTaxAssistantReplyStatusProto.PROCESSING]: 'Processing',
    [match.DEFAULT]: 'Idle',
  })
}
